import { Permissions } from '@webapp/platform/iam'
import { type ExportDataTypeConfig } from './ExportDataTypeConfig'
import { ExportTypes } from '../enums/ExportTypeEnum'

// Static per-type export configuration transcribed from Angular's
// `ExportModalOptions` (settings.exportcsv.services.js:121-767),
// `exportPermissionsMap` (:770-788) and `EXPORT_API_ENDPOINTS`. Insertion
// order matches the Angular landing rows (settings.exportcsv.home.tpl.html).
// Row titles/descriptions and filter labels are NOT stored here — they are
// intl messages in the consuming features, keyed by `key`/`fieldName`.
export const exportDataTypeConfigs = {
  order_items: {
    key: 'order_items',
    type: ExportTypes.ORDER_ITEMS,
    apiEndpointBase: '/api/v3/order-items',
    permission: Permissions.SETTINGS_EXPORT_DATA_REQUESTED_ORDER_ITEM,
    visibility: { entitlementKey: 'purchasing' },
    showFormat: true,
    filters: [
      {
        kind: 'select',
        fieldName: 'export_option',
        defaultValue: 0,
        options: [
          { value: 0 }, // All
          { value: 1 }, // Pending
          { value: 2 }, // Approved
          { value: 3 }, // Denied
          { value: 4 }, // Rejected
          { value: 5 }, // Pending Receive
          { value: 6 }, // Partially Received
          { value: 7 }, // Fully Received
        ],
      },
      {
        kind: 'daterange',
        fieldName: 'order_created_date',
        prefillLastMonth: true,
      },
    ],
    pendoKey: 'export-order-items',
  },
  po_items: {
    key: 'po_items',
    type: ExportTypes.PO_ITEMS,
    apiEndpointBase: '/api/v3/order-items',
    permission: Permissions.SETTINGS_EXPORT_DATA_PURCHASED_ORDER_ITEM,
    visibility: { entitlementKey: 'purchasing' },
    showFormat: true,
    filters: [
      {
        kind: 'select',
        fieldName: 'export_option',
        defaultValue: 8,
        options: [
          { value: 8 }, // Purchased
          { value: 9 }, // Pending Receive
          { value: 10 }, // Partially Received
          { value: 11 }, // Fully Received
        ],
      },
      {
        kind: 'daterange',
        fieldName: 'po_created_date',
        prefillLastMonth: true,
      },
    ],
    // PO Items shares the Bills export-columns context (OQ-5d):
    // GET export-columns/?export_option=<ExportTypes.BILLS>
    formatExportOption: ExportTypes.BILLS,
    pendoKey: 'export-purchase-order-items',
  },
  expense_report_history: {
    key: 'expense_report_history',
    type: ExportTypes.EXPENSE_REPORT_HISTORY,
    apiEndpointBase: '/api/v3/expenses/items',
    permission: Permissions.SETTINGS_EXPORT_DATA_EXPENSE_REPORT,
    visibility: { entitlementKey: 'expense' },
    showFormat: true,
    filters: [
      {
        kind: 'select',
        fieldName: 'export_option',
        defaultValue: 0,
        options: [
          { value: 0 }, // Status / All
          { value: 1 }, // Status / Pending
          { value: 2 }, // Status / Approved
          { value: 3 }, // Status / Denied
          { value: 4 }, // Payment / Not Recorded
          { value: 5 }, // Payment / Recorded
        ],
      },
      {
        kind: 'daterange',
        fieldName: 'expensereport_created_date',
        prefillLastMonth: true,
      },
    ],
    pendoKey: 'export-expense-report-history',
  },
  travel_history: {
    key: 'travel_history',
    type: ExportTypes.TRAVEL_HISTORY,
    apiEndpointBase: '/api/v3/travels',
    permission: Permissions.SETTINGS_EXPORT_DATA_TRAVEL,
    // Travel visibility is keyed off the expense entitlement (Angular parity:
    // enableTravelExports = expense.enabled)
    visibility: { entitlementKey: 'expense' },
    showFormat: true,
    filters: [
      {
        kind: 'select',
        fieldName: 'export_option',
        defaultValue: 0,
        options: [
          { value: 0 }, // All
          { value: 1 }, // Pending
          { value: 2 }, // Approved
          { value: 3 }, // Denied
        ],
      },
      {
        kind: 'daterange',
        fieldName: 'created_date',
        prefillLastMonth: true,
      },
    ],
    pendoKey: 'export-travel-history',
  },
  budget: {
    key: 'budget',
    type: ExportTypes.BUDGET,
    apiEndpointBase: '/api/v3/accounts',
    permission: Permissions.SETTINGS_EXPORT_DATA_BUDGET,
    // Legacy Budget row is replaced by budgets/spend_data when
    // flexible spend tracking is on
    visibility: {
      requiredFeatureSwitchesOff: ['enable_flexible_spend_tracking'],
    },
    showFormat: true,
    filters: [],
    pendoKey: 'export-budget',
  },
  receiving_history: {
    key: 'receiving_history',
    type: ExportTypes.RECEIVING_HISTORY,
    apiEndpointBase: '/api/v3/purchase-orders',
    permission: Permissions.SETTINGS_EXPORT_DATA_PURCHASE_ORDER,
    visibility: { entitlementKey: 'purchasing' },
    showFormat: true,
    filters: [
      // Angular's options.daterange: single hard-required date_0/date_1 range
      {
        kind: 'daterange',
        fieldName: 'date',
        prefillLastMonth: false,
        required: true,
      },
    ],
    pendoKey: 'export-receiving-history',
  },
  catalog: {
    key: 'catalog',
    type: ExportTypes.CATALOG,
    apiEndpointBase: '/api/v3/catalog-items',
    permission: Permissions.SETTINGS_EXPORT_DATA_CATALOG,
    visibility: { entitlementKey: 'purchasing' },
    showFormat: true,
    filters: [],
    pendoKey: 'export-catalog',
  },
  bills: {
    key: 'bills',
    type: ExportTypes.BILLS,
    apiEndpointBase: '/api/v2/ap/bills',
    permission: Permissions.SETTINGS_EXPORT_DATA_BILL,
    visibility: { entitlementKey: 'ap.expenseAp' },
    showFormat: true,
    filters: [
      { kind: 'toggle', fieldName: 'expense' }, // Expense Bills Only
      {
        kind: 'select',
        fieldName: 'is_exported', // Exported Bills Only
        defaultValue: null,
        options: [
          { value: null }, // All
          { value: true }, // Exported Bills Only
          { value: false }, // Unexported Bills Only
        ],
      },
      {
        kind: 'select',
        fieldName: 'has_posting_date', // Posted Bills
        defaultValue: null,
        options: [
          { value: null }, // All
          { value: true }, // Has Posting Date
          { value: false }, // Has No Posting Date
        ],
        requiredFeatureSwitch: 'gl_posting_date',
      },
      { kind: 'remote', fieldName: 'vendor', source: 'vendor' },
      { kind: 'remote', fieldName: 'currency', source: 'currency' },
      { kind: 'remote', fieldName: 'user', source: 'user' }, // Created By
      // Bills date ranges are NOT prefilled — Angular's last-month default
      // excludes BILLS (_exporter.js:355), confirmed by the observed preview
      // request carrying no date params
      { kind: 'daterange', fieldName: 'invoice_date', prefillLastMonth: false },
      {
        kind: 'daterange',
        fieldName: 'gl_post_date', // Posting Date
        prefillLastMonth: false,
        requiredFeatureSwitch: 'gl_posting_date',
      },
      { kind: 'daterange', fieldName: 'due_date', prefillLastMonth: false },
      {
        kind: 'daterange',
        fieldName: 'submitted_date',
        prefillLastMonth: false,
      },
      {
        kind: 'daterange',
        fieldName: 'last_export_date',
        prefillLastMonth: false,
      },
      {
        kind: 'select',
        fieldName: 'export_option', // Bill Status
        defaultValue: 0,
        options: [
          { value: 0 }, // All Active
          { value: 1 }, // Pending
          { value: 2 }, // Approved
          { value: 3 }, // Approved/Paid
          { value: 4 }, // Denied
          { value: 5 }, // Paid
          { value: 6 }, // Archived
        ],
      },
      { kind: 'toggle', fieldName: 'exclude_bill_cost' }, // Exclude bill allocations
    ],
    supportsMarkBillsAsExported: true,
    pendoKey: 'export-bills',
  },
  users: {
    key: 'users',
    type: ExportTypes.USERS,
    apiEndpointBase: '/api/v3/users',
    permission: Permissions.SETTINGS_EXPORT_DATA_USER_PROFILE,
    visibility: {},
    showFormat: false, // no format selector / preview / row count (BHV-039)
    filters: [],
    pendoKey: 'export-users',
  },
  locations: {
    key: 'locations',
    type: ExportTypes.LOCATIONS,
    apiEndpointBase: '/api/v3/locations',
    permission: Permissions.SETTINGS_EXPORT_DATA_LOCATION,
    visibility: {},
    showFormat: true,
    filters: [],
    pendoKey: 'export-locations',
  },
  receive_log: {
    key: 'receive_log',
    type: ExportTypes.RECEIVE_LOG,
    apiEndpointBase: '/api/v3/receive-logs-export/', // Angular parity incl. trailing slash (OQ-5b)
    permission: Permissions.SETTINGS_EXPORT_DATA_RECEIVE_LOG,
    visibility: { entitlementKey: 'purchasing' },
    showFormat: true,
    filters: [
      { kind: 'remote', fieldName: 'vendor', source: 'vendor' },
      {
        kind: 'daterange',
        fieldName: 'received_date',
        prefillLastMonth: false,
      },
    ],
    pendoKey: 'export-receive-log',
  },
  unbilled_po_items: {
    key: 'unbilled_po_items',
    type: ExportTypes.UNBILLED_PO_ITEMS,
    apiEndpointBase: '/api/v2/ap/items',
    permission: Permissions.SETTINGS_EXPORT_DATA_UNBILLED_BILL_ITEM,
    visibility: { entitlementKey: 'ap.everythingButExpense' },
    showFormat: true,
    filters: [
      {
        kind: 'select',
        fieldName: 'export_option',
        defaultValue: 0,
        options: [
          { value: 0 }, // All
          { value: 1 }, // All Active
          { value: 3 }, // Active Accruals
          { value: 2 }, // Archived
        ],
      },
      {
        kind: 'daterange',
        fieldName: 'received_date',
        prefillLastMonth: false,
      },
      { kind: 'remote', fieldName: 'vendor', source: 'vendor' },
    ],
    pendoKey: 'export-unbilled-po-items-(accruals-report)',
  },
  audit_importlog: {
    key: 'audit_importlog',
    type: ExportTypes.IMPORT_LOG,
    apiEndpointBase: '/api/v3/imports',
    visibility: {
      superuserOnly: true,
      requiredFeatureFlag: 'ARC_80_ENABLE_IMPORT_AUDIT_LOG_CSV_EXPORT',
    },
    showFormat: true,
    filters: [],
    pendoKey: 'export-import-audit-logs',
  },
  admin_auditlog: {
    key: 'admin_auditlog',
    type: ExportTypes.ADMIN_AUDIT_LOG,
    apiEndpointBase: '/api/v3/request-line-item-logs',
    // Superuser-only in practice — Angular's ARC_84_AUDIT_LOG_EXPORT read is a
    // dangling no-op statement (OQ-5a)
    visibility: { superuserOnly: true },
    showFormat: true,
    filters: [
      { kind: 'daterange', fieldName: 'changed_on', prefillLastMonth: false },
    ],
    pendoKey: 'export-procurify-administrator-audit-logs',
  },
  superuser_auditlog: {
    key: 'superuser_auditlog',
    type: ExportTypes.SUPERUSER_AUDIT_LOG,
    apiEndpointBase: '/api/v3/superuser-audit-logs',
    visibility: { superuserOnly: true },
    showFormat: true,
    filters: [
      { kind: 'daterange', fieldName: 'changed_on', prefillLastMonth: false },
    ],
    pendoKey: 'export-superuser-audit-logs',
  },
  spending_card_transactions: {
    key: 'spending_card_transactions',
    type: ExportTypes.SPENDING_CARD_TRANSACTIONS,
    apiEndpointBase: '/api/v3/pay/transactions',
    permission: Permissions.SETTINGS_EXPORT_DATA_TRANSACTION,
    visibility: {
      entitlementKey: 'spendingCard',
      requiredFeatureSwitchesAny: ['enable_airwallex_issuing'],
    },
    showFormat: true,
    filters: [
      {
        kind: 'daterange',
        fieldName: 'transaction_datetime',
        prefillLastMonth: false,
      },
      { kind: 'remote', fieldName: 'card', source: 'card' },
    ],
    pendoKey: 'export-spending-card-transactions',
  },
  budgets: {
    key: 'budgets',
    type: ExportTypes.BUDGET_CATEGORIES,
    apiEndpointBase: '/api/v3/budget-categories',
    permission: Permissions.SETTINGS_EXPORT_DATA_BUDGET,
    visibility: {
      requiredFeatureSwitchesAny: ['enable_flexible_spend_tracking'],
    },
    showFormat: true,
    filters: [
      { kind: 'daterange', fieldName: 'date_range', prefillLastMonth: false },
      {
        kind: 'select',
        fieldName: 'source_of_truth', // Category type
        defaultValue: null,
        options: [
          { value: null }, // All types
          { value: true }, // Only source of truth
          { value: false }, // Exclude source of truth
        ],
      },
    ],
    pendoKey: 'export-budget-setup',
  },
  spend_data: {
    key: 'spend_data',
    type: ExportTypes.CATEGORY_SPEND_DATA,
    apiEndpointBase: '/api/v3/budget-category-spend',
    permission: Permissions.SETTINGS_EXPORT_DATA_BUDGET,
    visibility: {
      requiredFeatureSwitchesAny: ['enable_flexible_spend_tracking'],
    },
    showFormat: true,
    filters: [
      { kind: 'daterange', fieldName: 'date_range', prefillLastMonth: false },
      {
        kind: 'select',
        fieldName: 'source_of_truth', // Category type
        defaultValue: null,
        options: [
          { value: null }, // All types
          { value: true }, // Only source of truth
          { value: false }, // Exclude source of truth
        ],
      },
    ],
    pendoKey: 'export-budget-spend-breakdown',
  },
  contracts: {
    key: 'contracts',
    type: ExportTypes.CONTRACTS,
    apiEndpointBase: '/api/v3/contracts-export',
    permission: Permissions.SETTINGS_EXPORT_DATA_CONTRACT,
    visibility: {
      entitlementKey: 'contracts',
      requiredFeatureSwitchesOff: ['disable_contract_management'],
    },
    showFormat: true,
    filters: [],
    pendoKey: 'export-contracts',
  },
} satisfies Record<string, ExportDataTypeConfig>

export type ExportDataTypeKey = keyof typeof exportDataTypeConfigs
